import { useMemo } from 'react';
import MojitoCore from 'mojito/core';
import DateTimeFormatter from 'presentation/formatters/date-time/index.js';
import EventFormatter from 'presentation/formatters/event/formatter.js';
import Timer from 'presentation/components/timer/index.js';
import FlexPane from 'presentation/components/flex-pane/index.js';
import Text from 'presentation/components/text/index.js';
import ParticipantsBoard from 'presentation/components/scoreboard/participants-board/index.js';
import NeutralVenueIndicator from 'presentation/components/neutral-venue-indicator';
import { sortParticipants } from 'presentation/components/scoreboard/utils.js';
import { getSeparatorTranslationKey } from 'presentation/formatters/participant-separator/index.js';
import { DURATION } from 'presentation/formatters/date-time/types.js';
import CompetitorIcon from 'presentation/components/competitor-icon/index.js';
import { useCompetitorsIconsQuery } from 'presentation/components/competitor-icon/hooks/index.js';
import RedCard from 'presentation/components/red-card/index.js';

const { UIViewImplementation } = MojitoCore.Presentation;
const SPORT_IDS = {
    GOLF: 'golf',
};

export default class GenericInplayScoreboard extends UIViewImplementation {
    constructor(props) {
        super(props);
    }

    getTimePart(event) {
        const { details = {} } = event;
        const { isClockRunning, clockDirection } = details;
        const { inplayTime, hasClockDetails } = this.props;

        if (hasClockDetails) {
            return (
                <Timer
                    config={this.config.inPlayClock}
                    time={inplayTime}
                    direction={clockDirection}
                    running={isClockRunning}
                />
            );
        } else if (inplayTime) {
            return DateTimeFormatter.formatDuration(
                inplayTime,
                this.appContext().userSettings(),
                this.appContext().systemSettings().language,
                DURATION.MINUTE
            );
        }
    }

    renderPeriodAndTime() {
        const { event } = this.props;
        const { details } = event;

        if (!details) {
            return null;
        }

        const periodPart =
            !this.config.hidePeriodForSport?.includes(event.sportId) &&
            EventFormatter.getDescription(event.sportId, details.inplayDescription, this.l10n);
        const timePart = this.getTimePart(event);
        const content = [];

        if (periodPart && timePart) {
            content.push(
                <div key={'description'} style={this.style.segmentDataFirst}>
                    {periodPart}
                </div>
            );
            content.push(
                <div key={'duration'} style={this.style.segmentDataLast}>
                    {timePart}
                </div>
            );
        } else {
            const segmentData = periodPart || timePart;
            segmentData &&
                content.push(
                    <div key={'segmentData'} style={this.style.segmentDataOnly}>
                        {segmentData}
                    </div>
                );
        }
        const segmentInfoStyle =
            this.props.event.sportId === SPORT_IDS.GOLF
                ? this.style.segmentsInfoCenter
                : this.style.segmentsInfo;
        return <FlexPane config={segmentInfoStyle}>{content}</FlexPane>;
    }

    renderThreeParticipantsScoreboard() {
        return (
            <ParticipantsBoard
                event={this.props.event}
                config={this.config.threeParticipantsScoreboard}
            />
        );
    }

    renderMainContent() {
        return (
            <MainContent
                event={this.props.event}
                config={this.config}
                styles={this.style}
                stringResolver={this.mojitoTools.stringResolver}
            />
        );
    }

    render() {
        const { event } = this.props;

        if (!event || !event.details) {
            return <div className="GenericInplayScoreboard ta-GenericInplayScoreboard" />;
        }

        const hasThreeParticipants = event?.participants?.length === 3;

        return (
            <FlexPane
                config={this.config.container}
                class="GenericInplayScoreboard ta-GenericInplayScoreboard"
            >
                {this.props.showEventIndicators && (
                    <FlexPane config={this.config.innerContainer}>
                        {this.renderPeriodAndTime()}
                        {event.neutral && (
                            <NeutralVenueIndicator config={this.config.neutralVenueIndicator} />
                        )}
                    </FlexPane>
                )}

                {hasThreeParticipants
                    ? this.renderThreeParticipantsScoreboard()
                    : this.renderMainContent()}
            </FlexPane>
        );
    }
}

const ParticipantName = ({ participant, icon, position, showIcon, config, styles }) => (
    <div style={styles.participantName[position]}>
        {showIcon && (
            <CompetitorIcon title={participant?.name} config={config.competitorIcon} src={icon} />
        )}
        <Text class="ta-participantName" config={config.participantNameText}>
            {participant?.name}
        </Text>
    </div>
);

const Scores = ({ event, config, styles, stringResolver }) => {
    const participants = useMemo(
        () => sortParticipants(event.participants, event.homeAway),
        [event.homeAway, event.participants]
    );

    const getScore = index => {
        if (!participants || !event.details?.totalScores) {
            return config.defaultNoScoreCharacter;
        }
        return event.details.totalScores[participants[index].position.toLowerCase()];
    };

    const redCardsHome = event.details.redCards?.home || 0;
    const redCardsAway = event.details.redCards?.away || 0;

    return (
        <FlexPane config={config.scoresContainer}>
            <RedCard cardsNumber={redCardsHome} />
            <FlexPane config={styles.scores}>
                <div className="ta-score" style={styles.score}>
                    {getScore(0)}
                </div>
                <div style={styles.scoreSeparator}>
                    {stringResolver.resolveString(getSeparatorTranslationKey(event.homeAway))}
                </div>
                <div className="ta-score" style={styles.score}>
                    {getScore(1)}
                </div>
            </FlexPane>
            <RedCard cardsNumber={redCardsAway} />
        </FlexPane>
    );
};

const NonstandardPeriodDurations = ({ event, config, styles, stringResolver }) => {
    const { sportId, details = {} } = event;
    const standardPeriod = config.standardPeriods[sportId];
    const periodDuration = details.periodDurationInMinutes;

    // Do not render if no configuration or it is incorrect or standard duration
    if (
        !standardPeriod ||
        !standardPeriod.duration ||
        !periodDuration ||
        periodDuration === standardPeriod.duration
    ) {
        return null;
    }

    // Show periods in a short form, e.g. (2 x 40')
    const periodsLabel = stringResolver.resolveAndFormatString(
        '$GENERIC_INPLAY_SCOREBOARD.PERIOD_DURATIONS_FORMATTER',
        standardPeriod.count,
        periodDuration +
            stringResolver.resolveString('$GENERIC_INPLAY_SCOREBOARD.PERIOD_DURATION_SUFFIX')
    );

    return (
        <div style={styles.periodDurations} className="ta-GenericInplayScoreboard-period_durations">
            {stringResolver.resolveAndFormatString(
                '$GENERIC_INPLAY_SCOREBOARD.PERIOD_DURATIONS_WRAPPER',
                periodsLabel
            )}
        </div>
    );
};

const MainContent = ({ event, config, styles, stringResolver }) => {
    const showIcons = config.participantIcons.showParticipantIcons;

    const participants = useMemo(
        () => sortParticipants(event.participants, event.homeAway),
        [event.homeAway, event.participants]
    );

    const { icons } = useCompetitorsIconsQuery({
        sportId: event.sportId,
        competitors: showIcons ? participants : undefined,
        config: config.competitorIconOptions,
    });

    return (
        <FlexPane config={config.mainContent}>
            <ParticipantName
                participant={participants?.[0]}
                icon={icons[0]}
                position="left"
                showIcon={showIcons}
                config={config}
                styles={styles}
            />
            <FlexPane config={config.eventInfo}>
                <Scores
                    event={event}
                    config={config}
                    styles={styles}
                    stringResolver={stringResolver}
                />
                <NonstandardPeriodDurations
                    event={event}
                    config={config}
                    styles={styles}
                    stringResolver={stringResolver}
                />
            </FlexPane>
            <ParticipantName
                participant={participants?.[1]}
                icon={icons[1]}
                position="right"
                showIcon={showIcons}
                config={config}
                styles={styles}
            />
        </FlexPane>
    );
};

GenericInplayScoreboard.getStyle = function (config, applicationMode, merge) {
    const { participantName, score, segmentData, periodDurations, scoreSeparator } = config.style;
    const { primaryColor, secondaryColor, accentColor, scores, segmentsInfo } = config;

    const segmentDataStyle = merge(
        {
            whiteSpace: 'nowrap',
        },
        segmentData
    );

    const getParticipantNameStyle = (flexAlignment, textAlignment) =>
        merge(
            {
                flex: '1 1 100%',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                flexDirection: 'column',
                color: primaryColor,
                justifyContent: flexAlignment,
                wordBreak: 'break-word',
                textAlign: textAlignment,
            },
            participantName
        );

    return {
        participantName: {
            left: getParticipantNameStyle('flex-start', 'center'),
            right: getParticipantNameStyle('flex-start', 'center'),
        },
        score: merge(
            {
                backgroundColor: accentColor,
                borderRadius: '2px',
                minWidth: '26px',
                lineHeight: '21px',
                textAlign: 'center',
            },
            score
        ),
        segmentDataFirst: merge({ color: secondaryColor }, segmentDataStyle, {
            textAlign: 'right',
        }),
        segmentDataLast: merge({ color: secondaryColor }, segmentDataStyle, { textAlign: 'left' }),
        segmentDataOnly: merge({ color: secondaryColor }, segmentDataStyle, {
            textAlign: 'center',
        }),
        periodDurations: merge(
            {
                color: accentColor,
                whiteSpace: 'nowrap',
            },
            periodDurations
        ),
        scores: merge(scores, {
            separator: {
                style: {
                    color: scores.separator.style.color || score.color || accentColor,
                },
            },
        }),
        scoreSeparator: merge({ color: secondaryColor }, scoreSeparator),
        segmentsInfo: merge(segmentsInfo, {
            style: {
                justifyContent: 'flex-start',
            },
            separator: {
                style: {
                    color: segmentsInfo.separator.style.color || score.color || accentColor,
                },
            },
        }),
        segmentsInfoCenter: merge(segmentsInfo, {
            style: {
                justifyContent: 'center',
            },
            separator: {
                style: {
                    color: segmentsInfo.separator.style.color || score.color || accentColor,
                },
            },
        }),
    };
};
